import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tv, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '../api/client';

export default function TVAuthPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();

  const [code, setCode] = useState(searchParams.get('code') || '');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Auto-format code as user types (XXXX-XXXX)
  const handleCodeChange = (value: string) => {
    // Remove non-alphanumeric characters except dash
    let cleaned = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');

    // Remove existing dashes for reformatting
    cleaned = cleaned.replace(/-/g, '');

    // Add dash after 4 characters
    if (cleaned.length > 4) {
      cleaned = cleaned.slice(0, 4) + '-' + cleaned.slice(4, 8);
    }

    setCode(cleaned);
    setStatus('idle');
    setErrorMessage('');
  };

  // Auto-submit if code came from URL
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode && isAuthenticated) {
      handleCodeChange(urlCode);
      // Small delay to let user see the code
      setTimeout(() => {
        handleSubmit(urlCode);
      }, 500);
    }
  }, [searchParams, isAuthenticated]);

  const handleSubmit = async (codeToSubmit?: string) => {
    const submitCode = codeToSubmit || code;

    if (!submitCode || submitCode.length < 9) {
      setErrorMessage(t('tv.invalidCode', 'Please enter a valid code'));
      setStatus('error');
      return;
    }

    if (!isAuthenticated) {
      setErrorMessage(t('tv.loginRequired', 'Please sign in first'));
      setStatus('error');
      return;
    }

    setStatus('loading');

    try {
      await apiClient.post('/auth/device/authorize', {
        userCode: submitCode,
      });

      setStatus('success');
    } catch (error: any) {
      setStatus('error');
      if (error.response?.status === 400) {
        setErrorMessage(t('tv.invalidOrExpiredCode', 'Invalid or expired code. Please try again.'));
      } else {
        setErrorMessage(t('tv.error', 'Something went wrong. Please try again.'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center">
            <Tv className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('tv.title', 'Connect Your TV')}
          </h1>
          <p className="text-gray-400">
            {t('tv.subtitle', 'Enter the code shown on your TV screen')}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          {status === 'success' ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {t('tv.success', 'TV Connected!')}
              </h2>
              <p className="text-gray-400 mb-6">
                {t('tv.successMessage', 'Your TV is now signed in. You can close this page.')}
              </p>
              <Link
                to="/home"
                className="inline-block px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium rounded-xl hover:from-primary-600 hover:to-secondary-600 transition-all"
              >
                {t('tv.goHome', 'Go to Home')}
              </Link>
            </div>
          ) : !isAuthenticated ? (
            // Not logged in
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-yellow-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {t('tv.loginRequired', 'Sign In Required')}
              </h2>
              <p className="text-gray-400 mb-6">
                {t('tv.loginRequiredMessage', 'Please sign in to connect your TV')}
              </p>
              <Link
                to={`/login?redirect=${encodeURIComponent(`/tv${code ? `?code=${code}` : ''}`)}`}
                className="inline-block px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium rounded-xl hover:from-primary-600 hover:to-secondary-600 transition-all"
              >
                {t('nav.signin', 'Sign In')}
              </Link>
            </div>
          ) : (
            // Code Input Form
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('tv.codeLabel', 'TV Code')}
              </label>

              <input
                type="text"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="XXXX-XXXX"
                maxLength={9}
                className="w-full px-4 py-4 bg-white/5 border border-white/20 rounded-xl text-white text-center text-2xl font-mono tracking-widest placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
                autoComplete="off"
                autoCapitalize="characters"
              />

              {status === 'error' && errorMessage && (
                <p className="mt-3 text-sm text-red-400 text-center">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'loading' || code.length < 9}
                className="w-full mt-6 px-6 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold text-lg rounded-xl hover:from-primary-600 hover:to-secondary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('tv.connecting', 'Connecting...')}
                  </>
                ) : (
                  t('tv.connect', 'Connect TV')
                )}
              </button>
            </form>
          )}
        </div>

        {/* Help Text */}
        <p className="text-center text-gray-500 text-sm mt-6">
          {t('tv.help', "Can't find the code? Look for it on your TV's sign-in screen.")}
        </p>
      </div>
    </div>
  );
}
